/**
 * OpenHoldModal — open a manual biologiste hold (POST /qc/retenues, cap qc.retenue.open). A mandatory
 * motif (≥ 2 chars, server-enforced) and a window (debut/fin, datetime-local). No QC run is involved;
 * this is the biologiste's discretionary containment. Gated by useCan.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Button, FormField, Input, Modal, Textarea, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { QC_CAPS } from "../caps";
import { useLabId, useOpenHold } from "../api";

export interface OpenHoldModalProps {
  open: boolean;
  onClose: () => void;
  onOpened: () => void;
}

export function OpenHoldModal({ open, onClose, onOpened }: OpenHoldModalProps) {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const canOpen = useCan(QC_CAPS.retenueOpen);
  const mutation = useOpenHold(labId);

  const [parametre, setParametre] = useState("");
  const [automate, setAutomate] = useState("");
  const [debut, setDebut] = useState("");
  const [fin, setFin] = useState("");
  const [motif, setMotif] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setParametre("");
    setAutomate("");
    setDebut("");
    setFin("");
    setMotif("");
    setTouched(false);
  }, [open]);

  const parametreError = touched && parametre.trim() === "" ? t("qc.field.required") : null;
  const debutError = touched && debut.trim() === "" ? t("qc.field.dateInvalid") : null;
  const finError = touched && fin.trim() === "" ? t("qc.field.dateInvalid") : null;
  const motifError = touched && motif.trim().length < 2 ? t("qc.field.required") : null;

  const valid =
    parametre.trim() !== "" && debut.trim() !== "" && fin.trim() !== "" && motif.trim().length >= 2;

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canOpen) return;
    mutation.mutate(
      {
        parametre_code: parametre.trim(),
        fenetre_debut: new Date(debut).toISOString(),
        fenetre_fin: new Date(fin).toISOString(),
        motif: motif.trim(),
        ...(automate.trim() ? { automate_id: automate.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t("qc.holds.open.success"));
          onOpened();
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("qc.field.required")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("qc.holds.open.title")}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
            {t("qc.action.cancel")}
          </Button>
          <Button
            type="submit"
            form="qc-open-hold"
            loading={mutation.isPending}
            disabled={!canOpen || !valid}
          >
            {t("qc.holds.open.submit")}
          </Button>
        </>
      }
    >
      <form id="qc-open-hold" onSubmit={submit} noValidate>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
          <FormField label={t("qc.parametre")} required {...(parametreError ? { error: parametreError } : {})}>
            {(p) => (
              <Input
                {...p}
                autoFocus
                mono
                autoComplete="off"
                placeholder={t("qc.parametre.placeholder")}
                value={parametre}
                invalid={!!parametreError}
                onChange={(e) => setParametre(e.target.value)}
              />
            )}
          </FormField>

          <FormField label={`${t("qc.automate")} (${t("qc.field.optional")})`}>
            {(p) => (
              <Input
                {...p}
                mono
                inputMode="numeric"
                autoComplete="off"
                placeholder={t("qc.automate.placeholder")}
                value={automate}
                onChange={(e) => setAutomate(e.target.value)}
              />
            )}
          </FormField>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--nv-s-3)" }}>
            <FormField label={t("qc.holds.field.debut")} required {...(debutError ? { error: debutError } : {})}>
              {(p) => (
                <Input
                  {...p}
                  type="datetime-local"
                  value={debut}
                  invalid={!!debutError}
                  onChange={(e) => setDebut(e.target.value)}
                />
              )}
            </FormField>
            <FormField label={t("qc.holds.field.fin")} required {...(finError ? { error: finError } : {})}>
              {(p) => (
                <Input
                  {...p}
                  type="datetime-local"
                  value={fin}
                  invalid={!!finError}
                  onChange={(e) => setFin(e.target.value)}
                />
              )}
            </FormField>
          </div>

          <FormField
            label={t("qc.holds.field.motif")}
            hint={t("qc.holds.field.motif.hint")}
            required
            {...(motifError ? { error: motifError } : {})}
          >
            {(p) => (
              <Textarea
                {...p}
                value={motif}
                invalid={!!motifError}
                onChange={(e) => setMotif(e.target.value)}
              />
            )}
          </FormField>
        </div>
      </form>
    </Modal>
  );
}
